import React, { useState, useEffect, useMemo } from "react";
import { Dialog, DialogTitle, DialogContent, IconButton, Tabs, Tab, CircularProgress } from "@material-ui/core";
import { primary } from "../../styles/colors";
import SummaryCards from "./SummaryCards";
import DuePaymentsTable from "./DuePaymentsTable";
import PaymentDetailPopup from "./PaymentDetailPopup";
import { useDuePayments } from "../../context/DuePaymentsContext";
import { getPaymentStatus } from "./duePaymentsService";
import "./duePaymentsAnimations.css";
import { GridCloseIcon } from "@mui/x-data-grid";
import { Typography } from "@mui/material";
import ReportXLS from "./ReportXLS";
import moment from "moment";

const EXCEL_HEADERS = [
  "ENTITY NAME", 
  "PO NUMBER", 
  "BILL NUMBER", 
  "TYPE", 
  "AMOUNT", 
  "PAID", 
  "BALANCE", 
  "DUE DATE", 
  "ARRIVAL DATE", 
  "STATUS", 
  "DISCOUNT"
];

const HEADER_KEY_MAPPING = {
  "ENTITY NAME": "entityName",
  "PO NUMBER": "poId",
  "BILL NUMBER": "BillNo",
  "TYPE": "type",
  "AMOUNT": "amount",
  "PAID": "amountPaid",
  "BALANCE": "amountDue",
  "DUE DATE": "dueDate",
  "ARRIVAL DATE": "dispatchDate",
  "STATUS": "status",
  "DISCOUNT": "discount" // Handled dynamically for sub-properties if needed
};

const DuePaymentsModal = ({ 
  open, 
  onClose, 
  payments, 
  summary, 
  loading, 
  loadingMore,
  pagination,
  onFilterChange,
  onLoadMore,
  useMockData 
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [detailPopupOpen, setDetailPopupOpen] = useState(false);
  const { dispatch } = useDuePayments();

  const tabs = [
    { label: "All", type: "all" },
    { label: "Millers", type: "miller" },
    { label: "Brokers", type: "broker" },
  ];

  const currentFilter = tabs[activeTab].type;

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    const filterType = tabs[newValue].type;
    if (onFilterChange) {
      onFilterChange(filterType);
    }
  };

  const filteredPayments = useMemo(() => {
    if (useMockData) {
      switch (activeTab) {
        case 1:
          return payments.filter((p) => p.type?.toLowerCase() === "miller");
        case 2:
          return payments.filter((p) => p.type?.toLowerCase() === "broker");
        default:
          return payments;
      }
    }
    return payments;
  }, [payments, activeTab, useMockData]);

  const filteredSummary = useMemo(() => {
    const filtered = filteredPayments;
    const totalDue = filtered.reduce((sum, p) => sum + (p.amountDue - (p.amountPaid || 0)), 0);
    const overdueCount = filtered.filter((p) => {
      const status = getPaymentStatus(p.dueDate);
      return status === "overdue";
    }).length;
    const urgentCount = filtered.filter((p) => {
      const status = getPaymentStatus(p.dueDate);
      return status === "urgent";
    }).length;
    const potentialSavings = filtered.reduce((sum, p) => sum + (p.discount?.savings || 0), 0);
    const millerDueCount = filtered.filter((p) => p.type?.toLowerCase() === "miller").length;
    const brokerDueCount = filtered.filter((p) => p.type?.toLowerCase() === "broker").length;

    return {
      totalDue,
      overdueCount,
      urgentCount,
      potentialSavings,
      millerDueCount,
      brokerDueCount,
    };
  }, [filteredPayments]);

  const handleRowClick = (payment) => {
    setSelectedPayment(payment);
    setDetailPopupOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailPopupOpen(false);
    setSelectedPayment(null);
  };

  const urgentCount = summary?.overdueCount + summary?.urgentCount || 0;

  const prepareDataForExcel = (dataList, headers) => {
    return dataList && dataList?.map((item) => {
      let row = {};
      
      headers.forEach((header) => {
        const correspondingKey = HEADER_KEY_MAPPING[header];
        let value = item[correspondingKey];

        // Custom formatting rules based on the header type
        if (header === "DUE DATE" || header === "BILL DATE" && value) {
          // Format ISO Date into a clean readable day string
          value = moment(value).format("DD MMM YYYY");
        } 
        else if (header === "DISCOUNT") {
          // Handles your nested discount sub-object safely
          value = item.discount?.eligible ? `${item.discount.percentage}%` : "0%";
        }
        else if (header === "STATUS" && item.dueDate) {
          // Capitalize status string cleanly (e.g., 'pending' -> 'PENDING')
          value = getPaymentStatus(item.dueDate)?.toUpperCase()
        }

        // Assign the value directly underneath the specific header name string
        row[header] = value !== undefined && value !== null ? value : "—";
      });

      return row;
    });
  };

  // useEffect(() => {
  //   if (urgentCount > 0) {
  //     dispatch({ type: "UPDATE_BELL_COUNT", payload: urgentCount });
  //   }
  // }, [urgentCount, dispatch]);

  const getFilterCounts = () => {
    const all = payments.length;
    const millers = payments.filter((p) => p.type?.toLowerCase() === "miller").length;
    const brokers = payments.filter((p) => p.type?.toLowerCase() === "broker").length;
    return [all, millers, brokers];
  };

  const [allCount, millersCount, brokersCount] = getFilterCounts();

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: "12px",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle
          disableTypography
          style={{
            p: "16px 24px",
            borderBottom: "1px solid #e9ecef",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 3,
            width: "100%",
          }}
        >
          <Typography
            variant="h6"
            component="span"
            style={{
              fontSize: "1.1rem",
              fontWeight: 600,
              color: primary,
            }}
          >
            Due Payments
          </Typography>

          <IconButton onClick={onClose} size="small" style={{ color: "#6c757d" }}>
            <GridCloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent style={{ padding: "20px" }}>
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "60px" }}>
              <CircularProgress color={primary} />
            </div>
          ) : (
            <>
              <div className="d-flex alignC justifySB" style={{ borderBottom: "1px solid #e9ecef", marginBottom: "16px" }}>
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  TabIndicatorProps={{
                    style: {
                      backgroundColor: primary,
                      height: "3px",
                      borderRadius: "3px 3px 0 0",
                    },
                  }}
                >
                  <Tab
                    label={
                      <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        All
                        <span
                          style={{
                            backgroundColor: activeTab === 0 ? "rgba(241, 154, 48, 0.2)" : "#E8E8E8",
                            borderRadius: "10px",
                            padding: "2px 8px",
                            fontSize: "0.7rem",
                          }}
                        >
                          {allCount} / {Number(summary.millerDueCount || 0) + Number(summary.brokerDueCount || 0)}
                        </span>
                      </span>
                    }
                    style={{
                      minWidth: "auto",
                      padding: "8px 16px",
                      fontSize: "13px",
                      textTransform: "capitalize",
                      fontWeight: 500,
                      color: "#6c757d",
                    }}
                  />
                  <Tab
                    label={
                      <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        Millers
                        <span
                          style={{
                            backgroundColor: activeTab === 1 ? "rgba(241, 154, 48, 0.2)" : "#E8E8E8",
                            borderRadius: "10px",
                            padding: "2px 8px",
                            fontSize: "0.7rem",
                          }}
                        >
                          {millersCount} / {Number(summary.millerDueCount || 0)}
                        </span>
                      </span>
                    }
                    style={{
                      minWidth: "auto",
                      padding: "8px 16px",
                      fontSize: "13px",
                      textTransform: "capitalize",
                      fontWeight: 500,
                      color: "#6c757d",
                    }}
                  />
                  <Tab
                    label={
                      <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        Brokers
                        <span
                          style={{
                            backgroundColor: activeTab === 2 ? "rgba(241, 154, 48, 0.2)" : "#E8E8E8",
                            borderRadius: "10px",
                            padding: "2px 8px",
                            fontSize: "0.7rem",
                          }}
                        >
                          {brokersCount} / {Number(summary.brokerDueCount || 0)}
                        </span>
                      </span>
                    }
                    style={{
                      minWidth: "auto",
                      padding: "8px 16px",
                      fontSize: "13px",
                      textTransform: "capitalize",
                      fontWeight: 500,
                      color: "#6c757d",
                    }}
                  />
                </Tabs>
                <div className="d-flex alignC justifyC" style={{ padding: "16px 0" }}>
                {console.log(filteredPayments, "summary")}
                  <ReportXLS 
                  header={EXCEL_HEADERS}
                  list={prepareDataForExcel(filteredPayments, EXCEL_HEADERS) || []}
                  Name={"Due Payments"}
                  />
                </div>
              </div>

              <SummaryCards
                // summary={filteredSummary}
                summary={summary}
                paymentsCount={filteredPayments.length}
                activeFilter={activeTab}
              />

              <DuePaymentsTable
                payments={filteredPayments}
                summary={summary}
                onRowClick={handleRowClick}
                onLoadMore={onLoadMore}
                loadingMore={loadingMore}
                hasMore={pagination?.next}
              />
            </>
          )}
        </DialogContent>
      </Dialog>

      <PaymentDetailPopup
        open={detailPopupOpen}
        onClose={handleCloseDetail}
        payment={selectedPayment}
      />
    </>
  );
};

export default DuePaymentsModal;
